import React, { useState, useEffect } from "react";
import numeral from "numeral";
import styles from "./BestyPricingCalculator.module.css";

const calculatePrice = (listings, option) => {
  if (listings === 0) return 0;

  let basePrice;
  if (listings <= 10) {
    basePrice = 3000 / 12;
  } else if (listings <= 50) {
    basePrice = 250 + (listings - 10) * 15;
  } else if (listings <= 100) {
    basePrice = 250 + 40 * 15 + (listings - 50) * 10;
  } else if (listings <= 250) {
    basePrice = 250 + 40 * 15 + 50 * 10 + (listings - 100) * 9;
  } else if (listings <= 499) {
    basePrice = 250 + 40 * 15 + 50 * 10 + 150 * 9 + (listings - 250) * 7;
  } else if (listings <= 999) {
    basePrice = 250 + 40 * 15 + 50 * 10 + 150 * 9 + 249 * 7 + (listings - 499) * 5;
  } else {
    basePrice = 250 + 40 * 15 + 50 * 10 + 150 * 9 + 249 * 7 + 500 * 5 + (listings - 999) * 3;
  }

  const price = Math.max(basePrice, (10000 / 12) * (listings >= 50 ? 1 : 0));

  let finalPrice;
  if (option === "option2") {
    finalPrice = price + listings * 3;
  } else if (option === "option3") {
    finalPrice = price + listings * 6; // Option 2 (price + listings * 3) + listings * 3
  } else {
    finalPrice = price;
  }

  // Apply 25% discount (make 25% cheaper)
  return finalPrice * 0.75;
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
};

export const BestyPricingCalculatorEuro = () => {
  const [listings, setListings] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [monthlyCost1, setMonthlyCost1] = useState(0);
  const [monthlyCost2, setMonthlyCost2] = useState(0);
  const [monthlyCost3, setMonthlyCost3] = useState(0);
  const [costPerListing1, setCostPerListing1] = useState(0);
  const [costPerListing2, setCostPerListing2] = useState(0);
  const [costPerListing3, setCostPerListing3] = useState(0);
  const [showQuarterly, setShowQuarterly] = useState(true);
  const [showAnnual, setShowAnnual] = useState(true);

  useEffect(() => {
    const newMonthlyCost1 = calculatePrice(listings, "option1") * (1 - discount / 100);
    const newMonthlyCost2 = calculatePrice(listings, "option2") * (1 - discount / 100);
    const newMonthlyCost3 = calculatePrice(listings, "option3") * (1 - discount / 100);

    setMonthlyCost1(newMonthlyCost1);
    setMonthlyCost2(newMonthlyCost2);
    setMonthlyCost3(newMonthlyCost3);
    setCostPerListing1(listings > 0 ? newMonthlyCost1 / listings : 0);
    setCostPerListing2(listings > 0 ? newMonthlyCost2 / listings : 0);
    setCostPerListing3(listings > 0 ? newMonthlyCost3 / listings : 0);
  }, [listings, discount]);

  return (
    <div className={styles.calculatorWrapper}>
      <h2 className={styles.calculatorTitle}>Besty Pricing Calculator</h2>
      <div className={styles.inputSection}>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>Listings</label>
          <input type="number" value={listings} onChange={(e) => setListings(Number(e.target.value) || 0)} className={styles.listingInput} placeholder="Enter Listing Count" />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>Discount %</label>
          <input type="number" value={discount} onChange={(e) => setDiscount(Number(e.target.value) || 0)} className={styles.discountInput} placeholder="Enter Discount (%)" />
        </div>
      </div>
      <div className={styles.toggleSection}>
        <label className={styles.toggleLabel}>
          <input type="checkbox" checked={showQuarterly} onChange={(e) => setShowQuarterly(e.target.checked)} className={styles.toggleCheckbox} />
          Show Quarterly Pricing
        </label>
        <label className={styles.toggleLabel}>
          <input type="checkbox" checked={showAnnual} onChange={(e) => setShowAnnual(e.target.checked)} className={styles.toggleCheckbox} />
          Show Annual Pricing
        </label>
      </div>
      <div className={styles.tableContainer}>
        <table className={styles.pricingTable}>
          <thead>
            <tr>
              <th></th>
              <th>Option 1</th>
              <th>Option 2</th>
              <th>Option 3</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={styles.rowLabel}>Cost per Listing (per month)</td>
              <td className={styles.copyableCell} onClick={() => copyToClipboard(numeral(costPerListing1).format("0.00"))} title="Click to copy">
                <span className={styles.priceValue}>€{numeral(costPerListing1).format("0.00")}</span>
              </td>
              <td className={styles.copyableCell} onClick={() => copyToClipboard(numeral(costPerListing2).format("0.00"))} title="Click to copy">
                <span className={styles.priceValue}>€{numeral(costPerListing2).format("0.00")}</span>
              </td>
              <td className={`${styles.option3Cell} ${styles.copyableCell}`} onClick={() => copyToClipboard(numeral(costPerListing3).format("0.00"))} title="Click to copy">
                <span className={styles.priceValue}>€{numeral(costPerListing3).format("0.00")}</span>
              </td>
            </tr>
            <tr>
              <td className={styles.rowLabel}>Monthly Price</td>
              <td className={styles.copyableCell} onClick={() => copyToClipboard(numeral(monthlyCost1).format("0,0.00"))} title="Click to copy">
                <span className={styles.priceValue}>€{numeral(monthlyCost1).format("0,0.00")}</span>
              </td>
              <td className={styles.copyableCell} onClick={() => copyToClipboard(numeral(monthlyCost2).format("0,0.00"))} title="Click to copy">
                <span className={styles.priceValue}>€{numeral(monthlyCost2).format("0,0.00")}</span>
              </td>
              <td className={`${styles.option3Cell} ${styles.copyableCell}`} onClick={() => copyToClipboard(numeral(monthlyCost3).format("0,0.00"))} title="Click to copy">
                <span className={styles.priceValue}>€{numeral(monthlyCost3).format("0,0.00")}</span>
              </td>
            </tr>
            {showQuarterly && (
              <tr>
                <td className={styles.rowLabel}>Quarterly Price</td>
                <td className={styles.copyableCell} onClick={() => copyToClipboard(numeral(monthlyCost1 * 0.95).format("0,0.00"))} title="Click to copy">
                  <span className={styles.priceValue}>€{numeral(monthlyCost1 * 0.95).format("0,0.00")}</span>
                </td>
                <td className={styles.copyableCell} onClick={() => copyToClipboard(numeral(monthlyCost2 * 0.95).format("0,0.00"))} title="Click to copy">
                  <span className={styles.priceValue}>€{numeral(monthlyCost2 * 0.95).format("0,0.00")}</span>
                </td>
                <td className={`${styles.option3Cell} ${styles.copyableCell}`} onClick={() => copyToClipboard(numeral(monthlyCost3 * 0.95).format("0,0.00"))} title="Click to copy">
                  <span className={styles.priceValue}>€{numeral(monthlyCost3 * 0.95).format("0,0.00")}</span>
                </td>
              </tr>
            )}
            {showAnnual && (
              <tr>
                <td className={styles.rowLabel}>Annual Price</td>
                <td className={styles.copyableCell} onClick={() => copyToClipboard(numeral(monthlyCost1 * 0.9).format("0,0.00"))} title="Click to copy">
                  <span className={styles.priceValue}>€{numeral(monthlyCost1 * 0.9).format("0,0.00")}</span>
                </td>
                <td className={styles.copyableCell} onClick={() => copyToClipboard(numeral(monthlyCost2 * 0.9).format("0,0.00"))} title="Click to copy">
                  <span className={styles.priceValue}>€{numeral(monthlyCost2 * 0.9).format("0,0.00")}</span>
                </td>
                <td className={`${styles.option3Cell} ${styles.copyableCell}`} onClick={() => copyToClipboard(numeral(monthlyCost3 * 0.9).format("0,0.00"))} title="Click to copy">
                  <span className={styles.priceValue}>€{numeral(monthlyCost3 * 0.9).format("0,0.00")}</span>
                </td>
              </tr>
            )}
            <tr className={styles.featureRow}>
              <td className={styles.rowLabel}>Upsell Percentage</td>
              <td>
                <span className={styles.percentageValue}>6%</span>
              </td>
              <td>
                <span className={styles.percentageValue}>3%</span>
              </td>
              <td className={styles.option3Cell}>
                <span className={styles.waivedBadge}>Waived</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {(!showQuarterly || !showAnnual) && (
        <div className={styles.infoSection}>
          {!showQuarterly && (
            <div className={styles.infoText}>
              <strong>Save 5% if you pay quarterly:</strong> Quarterly monthly cost × 3 = quarterly payments
            </div>
          )}
          {!showAnnual && (
            <div className={styles.infoText}>
              <strong>Save 10% if you pay annually:</strong> Annual monthly cost × 12 = annual payments
            </div>
          )}
        </div>
      )}
    </div>
  );
};
